import React, { useEffect, useMemo, useState } from "react";
import { Users, AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

export function computeProportionalTipSplits(cart, tipTotal) {
  const weights = {};
  cart.forEach((it) => {
    if (!it.employee_user_id) return;
    let lineSub = it.unit_price * it.qty;
    if (it.discount_type === "percent" && it.discount_value) {
      lineSub -= Math.min(it.discount_value, 100) / 100 * lineSub;
    } else if (it.discount_type === "amount" && it.discount_value) {
      lineSub -= Math.min(it.discount_value, lineSub);
    }
    weights[it.employee_user_id] = (weights[it.employee_user_id] || 0) + Math.max(0, lineSub);
  });
  const sum = Object.values(weights).reduce((a, b) => a + b, 0);
  if (sum <= 0 || tipTotal <= 0) return [];
  const entries = Object.entries(weights);
  const splits = entries.map(([employee_user_id, w]) => ({
    employee_user_id,
    amount: +(tipTotal * (w / sum)).toFixed(2),
  }));
  const allocated = splits.reduce((s, r) => s + r.amount, 0);
  const diff = +(tipTotal - allocated).toFixed(2);
  if (Math.abs(diff) >= 0.01 && splits.length) {
    splits[splits.length - 1].amount = +(splits[splits.length - 1].amount + diff).toFixed(2);
  }
  return splits.filter((s) => s.amount > 0);
}

export default function TipSplitModal({
  open,
  onClose,
  onConfirm,
  tipTotal,
  cart = [],
  staff = [],
  lang = "ar",
}) {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!open) return;
    const defaults = computeProportionalTipSplits(cart, tipTotal);
    if (defaults.length) {
      setRows(defaults);
    } else {
      const first = staff.find((s) => s.role === "staff") || staff[0];
      setRows(first ? [{ employee_user_id: first.user_id, amount: tipTotal > 0 ? tipTotal.toFixed(2) : "" }] : []);
    }
  }, [open, tipTotal, cart, staff]);

  const allocated = useMemo(
    () => rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0),
    [rows],
  );
  const remaining = +(tipTotal - allocated).toFixed(2);
  const canSubmit = rows.length >= 1
    && rows.every((r) => r.employee_user_id && parseFloat(r.amount) > 0)
    && Math.abs(remaining) < 0.02;

  const addRow = () => {
    const used = new Set(rows.map((r) => r.employee_user_id));
    const next = staff.find((s) => !used.has(s.user_id));
    setRows((prev) => [
      ...prev,
      { employee_user_id: next?.user_id || "", amount: remaining > 0 ? remaining.toFixed(2) : "" },
    ]);
  };

  const handleConfirm = () => {
    if (!canSubmit) return;
    onConfirm(rows.map((r) => ({
      employee_user_id: r.employee_user_id,
      amount: parseFloat(r.amount),
    })));
  };

  const staffName = (uid) => {
    const u = staff.find((s) => s.user_id === uid);
    return u?.name || u?.username || uid;
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="tip-split-modal">
        <DialogHeader>
          <DialogTitle className="font-heading gold-text flex items-center gap-2">
            <Users size={18}/>
            {lang === "ar" ? "توزيع الإكرامية" : "Distribute tip"}
          </DialogTitle>
          <DialogDescription className="text-[var(--text-muted)]">
            {lang === "ar"
              ? `قسّم ${tipTotal.toFixed(2)} ر.ق على الموظفين.`
              : `Split ${tipTotal.toFixed(2)} QAR among staff.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {rows.map((row, idx) => (
            <div key={idx} className="grid grid-cols-2 gap-2">
              <select
                className="input-dark text-sm py-1.5"
                value={row.employee_user_id}
                onChange={(e) => setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, employee_user_id: e.target.value } : r)))}
                data-testid={`tip-split-staff-${idx}`}
              >
                <option value="">{lang === "ar" ? "— موظف —" : "— Staff —"}</option>
                {staff.map((s) => (
                  <option key={s.user_id} value={s.user_id}>{s.name || s.username}</option>
                ))}
              </select>
              <input
                type="number"
                min="0"
                step="0.01"
                className="input-dark text-sm py-1.5 num-display"
                value={row.amount}
                onChange={(e) => setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, amount: e.target.value } : r)))}
                data-testid={`tip-split-amount-${idx}`}
              />
            </div>
          ))}
          <button
            type="button"
            onClick={addRow}
            className="text-xs text-[var(--gold)] hover:underline"
            data-testid="tip-split-add"
          >
            {lang === "ar" ? "+ إضافة موظف" : "+ Add staff"}
          </button>
          <div className={`flex justify-between text-sm rounded-lg px-3 py-2 border ${
            Math.abs(remaining) < 0.02 ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-800"
          }`}>
            <span>{lang === "ar" ? "المتبقي" : "Remaining"}</span>
            <span className="num-display font-semibold">{remaining.toFixed(2)}</span>
          </div>
          {!canSubmit && rows.length > 0 && Math.abs(remaining) >= 0.02 && (
            <div className="text-[11px] text-amber-700 flex items-center gap-1">
              <AlertCircle size={12}/>
              {lang === "ar" ? "يجب أن يساوي مجموع التوزيع قيمة الإكرامية" : "Split amounts must equal the tip total"}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 rounded-md text-sm">{lang === "ar" ? "إلغاء" : "Cancel"}</button>
          <button type="button" onClick={handleConfirm} disabled={!canSubmit} className="btn-gold px-4 py-2 rounded-md text-sm disabled:opacity-50" data-testid="tip-split-confirm">
            {lang === "ar" ? "تأكيد" : "Confirm"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
